import { useMutation, useQueryClient } from '@tanstack/react-query'
import { syncEoBuyouts, setSubgoalManualClose } from '../data/goalsApi.js'
import { useUIStore } from '../store/uiStore.js'
import { withArchiveGuard } from './_archiveGuard.js'

/**
 * useSyncEoBuyouts — fire the on-demand "Sync now" button. Cron runs
 * every 10 min so this is just a manual catch-up. After success the
 * goals query is invalidated so the new sub-goals show immediately.
 */
export function useSyncEoBuyouts() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async () => syncEoBuyouts(),
    onSuccess: () => {
      // Sync wrote new goals to KV — refetch from server.
      queryClient.invalidateQueries({ queryKey: ['goals'] })
    },
  })
  return withArchiveGuard(mutation)
}

/**
 * useSubgoalManualClose — flip the manual close override on a synced
 * sub-goal. Used by the "Mark closed" button.
 *
 *   const op = useSubgoalManualClose()
 *   op.mutate({ pillarName, goalIndex, subgoalIndex, manualClosed: true })
 *
 * Optimistic — flips current=1 in the cache immediately, server returns
 * the post-mutation goals array.
 */
export function useSubgoalManualClose() {
  const queryClient = useQueryClient()
  const currentUser = useUIStore((s) => s.currentUser)

  const mutation = useMutation({
    mutationFn: async ({ pillarName, goalIndex, subgoalIndex, manualClosed }) =>
      setSubgoalManualClose({
        pillarName,
        goalIndex,
        subgoalIndex,
        manualClosed,
        by: currentUser || null,
      }),

    onMutate: async ({ pillarName, goalIndex, subgoalIndex, manualClosed }) => {
      await queryClient.cancelQueries({ queryKey: ['goals'] })
      const previous = queryClient.getQueryData(['goals'])
      queryClient.setQueryData(['goals'], (old) => {
        if (!Array.isArray(old)) return old
        const next = structuredClone(old)
        const pillar = next.find((p) => p.pillar === pillarName)
        if (!pillar) return old
        const goal = pillar.goals?.[goalIndex]
        if (!goal) return old
        const subs = goal.subgoals || goal.subGoals
        const sub = subs?.[subgoalIndex]
        if (!sub) return old
        if (manualClosed) {
          sub.manualClosedAt = new Date().toISOString()
          sub.manualClosedBy = currentUser || null
          sub.current = 1
        } else {
          sub.manualClosedAt = null
          sub.manualClosedBy = null
          sub.current = sub.completedAt ? 1 : 0
        }
        // Recompute parent goal counter.
        const closedCount = (subs || []).filter((s) => s?.current === 1).length
        goal.current = closedCount
        return next
      })
      return { previous }
    },

    onSuccess: (serverGoals) => {
      if (Array.isArray(serverGoals)) {
        queryClient.setQueryData(['goals'], serverGoals)
      }
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['goals'], ctx.previous)
    },
  })
  return withArchiveGuard(mutation)
}
